import { Close, ContactMail, Email } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { useEmployeeTerminology } from "../../../utils/terminology";

interface QuoteRequestDialogProps {
  open: boolean;
  onClose: () => void;
  employee: any;
  chatId: string;
  companyId: string;
  onSubmitQuoteRequest: (data: {
    contactName: string;
    contactEmail: string;
    contactPhone?: string;
    message?: string;
  }) => Promise<void>;
}

const QuoteRequestDialog: React.FC<QuoteRequestDialogProps> = ({
  open,
  onClose,
  employee,
  chatId,
  companyId,
  onSubmitQuoteRequest,
}) => {
  const { employee: employeeText } = useEmployeeTerminology();
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!contactName.trim()) {
      setError("Contact name is required");
      return;
    }

    if (!contactEmail.trim()) {
      setError("Contact email is required");
      return;
    }

    if (!contactEmail.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await onSubmitQuoteRequest({
        contactName: contactName.trim(),
        contactEmail: contactEmail.trim(),
        contactPhone: contactPhone.trim() || undefined,
        message: message.trim() || undefined,
      });

      // Reset form
      setContactName("");
      setContactEmail("");
      setContactPhone("");
      setMessage("");
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to submit quote request");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setContactName("");
      setContactEmail("");
      setContactPhone("");
      setMessage("");
      setError("");
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "60vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pr: 6,
        }}
      >
        <ContactMail color="primary" />
        Request a Quote
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          disabled={loading}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>What happens next?</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              • Your conversation will be converted to a PDF
              <br />
              • The PDF will be emailed to the real person behind this AI
              employee
              <br />
              • They'll receive your contact information to follow up with a
              quote
              <br />• You may receive a reply directly to your email
            </Typography>
          </Alert>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 3,
            p: 2,
            borderRadius: 1,
          }}
        >
          <img
            src={employee?.picture}
            alt={employee?.name}
            style={{ width: 48, height: 48, borderRadius: "50%" }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <Box>
            <Typography variant="h6">
              {employee?.name || `AI ${employeeText}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {employee?.title || "AI Assistant"}
            </Typography>
          </Box>
        </Box>

        <TextField
          autoFocus
          margin="dense"
          label="Your Name"
          type="text"
          fullWidth
          variant="outlined"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          placeholder="Enter your full name"
          sx={{ mb: 2 }}
          disabled={loading}
          required
        />

        <TextField
          margin="dense"
          label="Your Email"
          type="email"
          fullWidth
          variant="outlined"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          placeholder="Enter your email address"
          helperText="This is where the service provider will contact you"
          sx={{ mb: 2 }}
          disabled={loading}
          required
        />

        <TextField
          margin="dense"
          label="Phone Number (Optional)"
          type="tel"
          fullWidth
          variant="outlined"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
          placeholder="Enter your phone number"
          sx={{ mb: 2 }}
          disabled={loading}
        />

        <TextField
          margin="dense"
          label="Additional Message (Optional)"
          multiline
          rows={4}
          fullWidth
          variant="outlined"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Add any specific requirements, timeline, or questions about the project..."
          helperText="This will be included in the email to help the provider understand your needs"
          disabled={loading}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          variant="contained"
          loading={loading}
          startIcon={<Email />}
        >
          Send Quote Request
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default QuoteRequestDialog;
